import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { type CSSProperties, Fragment } from 'react';

import Text from '~/components/Text';

interface Props {
  title: string;
  description: string | React.ReactElement<any> | number | string;
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactElement<any> | number | string;
  backgroundColor?: CSSProperties['backgroundColor'];
  textColor?: CSSProperties['color'];
  closeButtonTextColor?: CSSProperties['color'];
  closeButtonBackgroundColor?: CSSProperties['backgroundColor'];
}

const SlideUpModal = ({
  isOpen,
  onClose,
  children,
  title,
  description,
  backgroundColor,
  textColor,
  closeButtonTextColor,
  closeButtonBackgroundColor,
}: Props) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-50"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-50"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bottom-0 left-0 z-30 bg-black/60" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="transform sm:translate-y-0 translate-y-1/2 opacity-0"
          enterTo="transform translate-y-0 opacity-100"
          leave="ease-in duration-100"
          leaveFrom="transform translate-y-0 opacity-100"
          leaveTo="transform sm:translate-y-0 translate-y-1/2 opacity-0"
        >
          <div className="fixed inset-0 bottom-0 left-0 z-40 flex items-end overflow-y-auto sm:items-center">
            <Dialog.Panel
              className={classNames(
                'relative mx-auto flex w-full max-w-none transform justify-center overflow-auto rounded-t-wt p-8 text-left align-middle transition-all sm:max-w-xl sm:rounded-wt',
                'bg-wt-background',
              )}
              style={{
                backgroundColor,
              }}
            >
              <div className="relative w-full space-y-4">
                <div className="flex justify-between">
                  <div className="flex flex-col gap-1">
                    <Text as="h5" size="xl" className="text-wt-text-on-background" style={{ color: textColor }}>
                      {title}
                    </Text>
                    <Text
                      as="p"
                      size="md"
                      weight="regular"
                      className={classNames('font-regular', 'text-wt-text-on-background')}
                      style={{ color: textColor }}
                    >
                      {description}
                    </Text>
                  </div>

                  <div className="h-6 w-6">
                    <button
                      type="button"
                      aria-label="Close"
                      className={classNames(
                        'flex h-6 w-6 items-center justify-center rounded-wt transition-all',
                        !closeButtonBackgroundColor && 'hover:bg-black/5',
                      )}
                      style={{
                        backgroundColor: closeButtonBackgroundColor,
                      }}
                      onClick={onClose}
                    >
                      <XMarkIcon
                        className={classNames(
                          'h-5 w-5 stroke-1 transition-all',
                          !closeButtonTextColor && 'text-gray-400 hover:text-gray-700',
                        )}
                        style={{
                          color: closeButtonTextColor,
                        }}
                      />
                    </button>
                  </div>
                </div>

                {children}
              </div>
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default SlideUpModal;
